import { Suspense, lazy, useEffect, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router";
import type { OrderView, QuoteResponse } from "@townsy/contracts";
import { fetchOrder, fetchProperty, postOrder, postOrderCancel, postPaymentIntent, postQuote } from "../../api/endpoints";
import { useBootstrapQuery, usePaymentsConfigQuery, useWalletsQuery } from "../../api/queries";
import { useTownSession } from "../../app/TownSessionContext";
import { CheckoutController, INITIAL_CHECKOUT, isCardOnly, type CheckoutState, type QuoteTarget } from "../../commerce/checkoutMachine";
import type { CheckoutBillboard, CheckoutCampaign, CheckoutRequest, CheckoutSubject } from "../../commerce/checkoutRequest";
import { billboardName } from "../../advertise/media";
import { formatUtcPeriod } from "../../advertise/utcDays";
import { TAKEOVER_CONCEPT, canCollectCards } from "../../commerce/gates";
import { KIND_LABEL, canCancel } from "../../commerce/orderStatus";
import { makeIdempotencyKey } from "../../homes/commands";
import { rememberTaxAddress, rememberedTaxAddress } from "../../commerce/taxAddress";
import { formatCents } from "../../lib/format";
import { formatCountdown, msUntil, serverNow } from "../../lib/serverClock";
import { useTownStore } from "../../state/store";
import { Dialog } from "../Dialog";
import { OrderStatusView } from "./OrderStatusView";
import { SimulatedPaymentStep } from "./SimulatedPaymentStep";
import { TaxAddressStep } from "./TaxAddressStep";
import { useServerNow } from "./useServerNow";

const StripePaymentStep = lazy(() => import("./StripePaymentStep"));

/** Shown ONLY in Stripe test mode (ADR-0046: a live checkout never mentions test cards). */
export const TEST_CARD_HINT =
  "Stripe test mode: no real money moves. Use test card 4242 4242 4242 4242 with any future expiry date, any CVC and any postal code. For a 3D Secure check use 4000 0027 6000 3184; for a decline use 4000 0000 0000 0002.";

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/** A commercial quote must carry the building's current version: re-read it before a new quote. */
async function refreshTarget(target: QuoteTarget): Promise<QuoteTarget> {
  if (target.kind !== "COMMERCIAL_ACQUISITION") return target;
  const p = await fetchProperty(target.propertyId);
  return { ...target, expectedPropertyVersion: p.version };
}

/**
 * The dollar checkout (docs/01 section 6 "Purchase review", docs/04 sections 6-8): server quote ->
 * explicit confirmation -> idempotent order (property/credit held) -> Stripe Payment Element for
 * the card shortfall only -> polling the server until the order is fulfilled or refunded. An
 * all-credit purchase completes at the order step with no card form. The dialog is a modal
 * (`role="dialog"`, `data-movement-block`), and the store's `checkout` flag stops avatar movement
 * while it is open, including while typing in Stripe's iframe (A09).
 */
export function CheckoutDialog({ request, onClose, onOrder }: { request: CheckoutRequest; onClose(): void; onOrder(order: OrderView): void }) {
  const session = useTownSession();
  const boot = useBootstrapQuery();
  const signedIn = Boolean(boot.data?.session.userId);
  const configQ = usePaymentsConfigQuery();
  const walletsQ = useWalletsQuery(signedIn);
  const watchOrder = useTownStore((s) => s.watchOrder);
  const markReserved = useTownStore((s) => s.markReserved);
  const [state, setState] = useState<CheckoutState>(() => ({
    ...INITIAL_CHECKOUT,
    step: request.mode === "new" ? "quoting" : "processing",
    target: request.mode === "new" ? request.target : null,
  }));
  const [leaving, setLeaving] = useState(false);
  const ctrlRef = useRef<CheckoutController | null>(null);
  const onOrderRef = useRef(onOrder);
  onOrderRef.current = onOrder;
  // ADR-0046: a new checkout waits for the payments config (it says whether Stripe Tax needs the buyer's location
  // before any price). If the config cannot load, the server still answers TAX_ADDRESS_REQUIRED when it needs one.
  const configSettled = configQ.data !== undefined || configQ.isError || request.mode !== "new";
  const addressRequired = configQ.data?.taxAddressRequired === true;

  useEffect(() => {
    if (!configSettled) return;
    const ctrl = new CheckoutController(
      {
        api: {
          quote: postQuote,
          createOrder: postOrder,
          paymentIntent: postPaymentIntent,
          getOrder: (id) => fetchOrder(id),
          cancel: postOrderCancel,
        },
        now: serverNow,
        sleep,
        makeKey: () => makeIdempotencyKey("order"),
        onChange: (s) => {
          if (s.taxAddress) rememberTaxAddress(s.taxAddress);
          setState(s);
        },
        onOrder: (o) => onOrderRef.current(o),
        refreshTarget,
      },
      request.mode === "new" ? { target: request.target, addressRequired, taxAddress: addressRequired ? rememberedTaxAddress() : null } : { orderId: request.orderId },
    );
    ctrlRef.current = ctrl;
    void ctrl.start();
    return () => {
      ctrl.dispose();
      ctrlRef.current = null;
    };
    // The controller is created once per request, after the config settled (addressRequired is read then).
  }, [request, configSettled]);

  const ctrl = ctrlRef.current;
  const subject: CheckoutSubject | null = request.subject;
  const kind = state.quote?.kind ?? state.order?.kind ?? subject?.kind ?? null;
  // Campaign budgets are card only (docs/04 section 2): no Townsy Credit toggle, ever.
  const cardOnly = isCardOnly(state.target) || kind === "CAMPAIGN_FUNDING";
  const counting = state.step === "review" || state.step === "payment" || state.step === "preparing_payment" || state.step === "processing";
  const now = useServerNow(counting);

  // The quote's price is held for ~2 minutes: after that, confirming asks for a new price.
  const quoteExpiresAt = state.step === "review" && !state.quoteExpired ? (state.quote?.expiresAt ?? null) : null;
  useEffect(() => {
    if (!quoteExpiresAt) return;
    const t = setTimeout(() => ctrlRef.current?.markQuoteExpired(), msUntil(quoteExpiresAt) + 50);
    return () => clearTimeout(t);
  }, [quoteExpiresAt]);

  // The hold ends: ask the server (it may have been paid at the last second; never assume "expired").
  const holdEndsAt = state.step === "payment" ? (state.order?.reservationExpiresAt ?? null) : null;
  useEffect(() => {
    if (!holdEndsAt) return;
    const t = setTimeout(() => void ctrlRef.current?.reservationEnded(), msUntil(holdEndsAt) + 250);
    return () => clearTimeout(t);
  }, [holdEndsAt]);

  // Remember "someone else is checking out this property" for the property panel.
  const failureCode = state.failure?.code ?? null;
  useEffect(() => {
    if (failureCode === "PROPERTY_RESERVED" && subject?.propertyId) markReserved(subject.propertyId);
  }, [failureCode, subject?.propertyId, markReserved]);

  const inFlight = state.step === "ordering" || state.step === "confirming" || state.step === "canceling";
  const holdOpen = (state.step === "payment" || state.step === "preparing_payment") && state.order !== null;

  const requestClose = () => {
    if (inFlight) return;
    if (holdOpen) {
      setLeaving(true);
      return;
    }
    if (state.order && state.step === "processing") watchOrder(state.order.orderId);
    onClose();
  };

  const title = subject?.title ?? (state.order ? state.order.description || "Your order" : "Checkout");
  const config = configQ.data ?? null;
  const creditAvailable = walletsQ.data?.credit.availableCents ?? 0;
  // ADR-0046: what the buyer sees follows the mode the server reports for this order/quote (else the server's config).
  const live = (state.order?.mode ?? state.quote?.mode ?? config?.mode ?? "test") === "live";

  let body: ReactNode;
  let footer: ReactNode;

  if (leaving && holdOpen && state.order) {
    const order = state.order;
    body = (
      <div className="stack" data-testid="checkout-leave">
        <p style={{ margin: 0 }}>
          <strong>{order.description || "This purchase"}</strong> is held for you for another{" "}
          <strong>{formatCountdown(msUntil(order.reservationExpiresAt, now))}</strong>.
        </p>
        <p className="small" style={{ margin: 0 }}>
          Cancel it now to release the hold{order.creditAppliedCents > 0 ? " and your Townsy Credit" : ""}, or keep it and pay later from <strong>My orders</strong>{" "}
          before the hold ends. Nothing has been charged to your card yet.
        </p>
      </div>
    );
    footer = (
      <>
        <button
          type="button"
          className="btn btn-danger"
          onClick={() => {
            setLeaving(false);
            void ctrl?.cancel();
          }}
          data-testid="checkout-leave-cancel"
        >
          Cancel purchase
        </button>
        <button
          type="button"
          className="btn"
          onClick={() => {
            watchOrder(order.orderId);
            onClose();
          }}
        >
          Keep it and close
        </button>
        <button type="button" className="btn btn-primary" onClick={() => setLeaving(false)} data-autofocus>
          Back to payment
        </button>
      </>
    );
  } else {
    switch (state.step) {
      case "address":
        body = (
          <TaxAddressStep
            initial={state.taxAddress}
            serverMessage={state.failure?.action === "address" ? state.failure.message : null}
            busy={false}
            onSubmit={(address) => void ctrl?.submitTaxAddress(address)}
          />
        );
        footer = <CloseButton onClose={requestClose} label="Cancel" />;
        break;
      case "quoting":
        body = <StatusLine text={state.addressRequired ? "Getting a price with sales tax from Townsy…" : "Getting a price from Townsy…"} />;
        footer = <CloseButton onClose={requestClose} label="Cancel" />;
        break;
      case "review": {
        const q = state.quote!;
        const expired = state.quoteExpired || msUntil(q.expiresAt, now) <= 0;
        const retry = state.failure?.action === "retry";
        body = (
          <div className="stack" data-testid="checkout-review">
            <QuoteSummary
              quote={q}
              now={now}
              expired={expired}
              billboard={subject?.billboard ?? null}
              campaign={subject?.campaign ?? null}
              onChangeTaxAddress={state.addressRequired ? () => ctrl?.editTaxAddress() : undefined}
            />
            <CreditToggle
              visible={!cardOnly && (creditAvailable > 0 || q.creditAppliedCents > 0 || !state.useCredit)}
              useCredit={state.useCredit}
              availableCents={creditAvailable}
              onChange={(v) => void ctrl?.setUseCredit(v)}
            />
            {q.kind === "COMMERCIAL_ACQUISITION" ? <CommercialTerms quote={q} /> : <PurchaseTerms quote={q} />}
            <PaymentsFinal />
            <p className="hint" style={{ margin: 0 }} data-testid="checkout-notice">
              {config?.notice ?? (live ? "" : "Stripe test mode: no real money moves.")}
              {live ? "" : " Prices are development test values."}
            </p>
            <FailureNotice state={state} />
          </div>
        );
        footer = (
          <>
            <button type="button" className="btn btn-primary" onClick={() => void ctrl?.confirm()} data-testid="checkout-confirm" data-autofocus>
              {expired ? "Get a new price" : retry ? "Try again" : confirmLabel(q)}
            </button>
            <CloseButton onClose={requestClose} label="Cancel" />
          </>
        );
        break;
      }
      case "ordering":
        body = <StatusLine text={state.quote && state.quote.cardAmountCents === 0 ? "Completing your purchase with Townsy Credit…" : "Reserving it for you…"} />;
        footer = null;
        break;
      case "preparing_payment":
        body = (
          <div className="stack">
            {state.order ? <HoldLine order={state.order} now={now} /> : null}
            {state.failure ? <FailureNotice state={state} /> : <StatusLine text="Preparing the secure card form…" />}
          </div>
        );
        footer = state.failure ? (
          <>
            <button type="button" className="btn btn-primary" onClick={() => void ctrl?.retryPreparePayment()}>
              Try again
            </button>
            <button type="button" className="btn" onClick={() => void ctrl?.cancel()}>
              Cancel purchase
            </button>
          </>
        ) : null;
        break;
      case "payment":
      case "confirming": {
        const order = state.order!;
        const intent = state.intent;
        const confirming = state.step === "confirming";
        const simulated = config?.provider === "simulated";
        const cardsOk = !config || canCollectCards(config);
        body = (
          <div className="stack" data-testid="checkout-payment">
            <HoldLine order={order} now={now} />
            <dl className="kv">
              <dt>Purchase</dt>
              <dd>
                <strong>{order.description}</strong>
              </dd>
              <dt>Townsy Credit</dt>
              <dd className="price">{cardOnly ? "Not used (card only)" : `${formatCents(order.creditAppliedCents)} (held for this order)`}</dd>
              {order.taxCents > 0 ? (
                <>
                  <dt>Sales tax</dt>
                  <dd className="price" data-testid="checkout-payment-tax">
                    {formatCents(order.taxCents)} <span className="small muted">(included in the card amount)</span>
                  </dd>
                </>
              ) : null}
              <dt>Card pays</dt>
              <dd className="price">
                <strong>{formatCents(order.cardAmountCents)}</strong>
              </dd>
            </dl>
            {simulated || live ? null : (
              <div className="notice notice-info small" data-testid="test-card-hint">
                {TEST_CARD_HINT}
              </div>
            )}
            {state.cardMessage ? (
              <div className="notice notice-danger" role="alert" data-testid="checkout-card-error">
                {state.cardMessage}
              </div>
            ) : null}
            <FailureNotice state={state} />
            {intent && simulated ? (
              // Simulated payments exist only in local development (always test mode).
              <SimulatedPaymentStep
                orderId={order.orderId}
                amountLabel={formatCents(order.cardAmountCents)}
                busy={confirming}
                onConfirmStart={() => ctrlRef.current?.beginConfirm() ?? false}
                onConfirmResult={(outcome) => void ctrlRef.current?.finishConfirm(outcome)}
              />
            ) : intent && cardsOk ? (
              <Suspense fallback={<StatusLine text="Loading the secure card form…" />}>
                <StripePaymentStep
                  publishableKey={intent.publishableKey}
                  clientSecret={intent.clientSecret}
                  payLabel={live ? `Pay ${formatCents(order.cardAmountCents)}` : `Pay ${formatCents(order.cardAmountCents)} (test card)`}
                  returnUrl={`${window.location.origin}/orders/${encodeURIComponent(order.orderId)}?return=1`}
                  busy={confirming}
                  onConfirmStart={() => ctrlRef.current?.beginConfirm() ?? false}
                  onConfirmResult={(outcome) => void ctrlRef.current?.finishConfirm(outcome)}
                />
              </Suspense>
            ) : (
              <div className="notice notice-warning" role="status">
                The card form is not available on this server (payment provider: {config?.provider ?? "unknown"}). Nothing was charged. Cancel this
                purchase, or let the hold end by itself.
              </div>
            )}
            {confirming ? <StatusLine text="Waiting for your bank. If a 3D Secure window opens, complete it there." /> : null}
          </div>
        );
        footer = (
          <button type="button" className="btn" disabled={confirming} onClick={() => void ctrl?.cancel()} data-testid="checkout-cancel-order">
            Cancel purchase
          </button>
        );
        break;
      }
      case "processing":
        body = (
          <div className="stack" data-testid="checkout-processing">
            {state.pollTimedOut ? (
              <div className="notice notice-warning" role="status">
                Still waiting for the payment provider's confirmation. You can close this window: the order page keeps checking, and your purchase
                completes (or is refunded to your card) on the server either way.
              </div>
            ) : (
              <StatusLine text="Checking with Townsy… The purchase completes only when the payment is confirmed to Townsy's server." />
            )}
            {state.cardMessage ? <p className="small muted" style={{ margin: 0 }}>{state.cardMessage}</p> : null}
            {state.failure ? (
              <p className="small error-text" style={{ margin: 0 }}>
                {state.failure.message}
              </p>
            ) : null}
            {state.order ? <OrderStatusView order={state.order} now={now} compact modeBadge={false} /> : null}
          </div>
        );
        footer = (
          <>
            {state.pollTimedOut ? (
              <button type="button" className="btn btn-primary" onClick={() => void ctrl?.checkAgain()}>
                Check again
              </button>
            ) : null}
            {state.order ? (
              <Link className="btn" to={`/orders/${state.order.orderId}`} onClick={() => state.order && watchOrder(state.order.orderId)}>
                Order page
              </Link>
            ) : null}
            <CloseButton onClose={requestClose} label="Close" />
          </>
        );
        break;
      case "canceling":
        body = <StatusLine text="Asking to cancel… If the payment already went through, the purchase completes instead." />;
        footer = null;
        break;
      case "settled": {
        const order = state.order!;
        body = (
          <div className="stack" data-testid="checkout-settled">
            {order.status === "fulfilled" ? <p style={{ margin: 0 }}>{fulfilledLine(order, subject)}</p> : null}
            <OrderStatusView order={order} now={now} modeBadge={false} />
          </div>
        );
        footer = (
          <>
            {order.status === "fulfilled" && order.kind === "COMMERCIAL_ACQUISITION" && order.propertyId ? (
              <>
                <SetUpStoreButton propertyId={order.propertyId} onDone={onClose} />
                <ChooseInteriorButton propertyId={order.propertyId} onDone={onClose} />
              </>
            ) : null}
            {order.status === "fulfilled" ? <FollowUp order={order} subject={subject} onDone={onClose} hasSession={Boolean(session)} /> : null}
            <Link className="btn" to={`/orders/${order.orderId}`} onClick={onClose}>
              Receipt page
            </Link>
            <CloseButton onClose={onClose} label="Done" primary={order.status !== "fulfilled"} />
          </>
        );
        break;
      }
      case "failed": {
        const f = state.failure;
        body = (
          <div className="stack" data-testid="checkout-failed">
            <div className="notice notice-danger" role="alert" data-testid="checkout-error" data-code={f?.code ?? ""}>
              {f?.message ?? "The purchase could not be completed."}
            </div>
            {f?.code === "VALIDATION_FAILED" && !state.useCredit && !cardOnly ? (
              <CreditToggle visible useCredit={state.useCredit} availableCents={creditAvailable} onChange={(v) => void ctrl?.setUseCredit(v)} />
            ) : null}
            {f?.code === "RESERVATION_LIMIT" ? (
              <p className="small" style={{ margin: 0 }}>
                <Link to="/orders" onClick={onClose}>
                  Open My orders
                </Link>
              </p>
            ) : null}
            {state.order ? <OrderStatusView order={state.order} now={now} compact modeBadge={false} /> : null}
          </div>
        );
        footer = (
          <>
            {f && f.action !== "close" ? (
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => void (state.order ? ctrl?.checkAgain() : ctrl?.requote())}
                data-testid="checkout-retry"
              >
                {f.action === "requote" ? "Get a new price" : f.action === "refresh_order" ? "Check the order" : "Try again"}
              </button>
            ) : null}
            {state.order && canCancel(state.order) ? (
              <button type="button" className="btn" onClick={() => void ctrl?.cancel()}>
                Cancel purchase
              </button>
            ) : null}
            <CloseButton onClose={onClose} label="Close" primary={!f || f.action === "close"} />
          </>
        );
        break;
      }
    }
  }

  return (
    <Dialog
      title={title}
      onClose={requestClose}
      describedBy="checkout-body"
      closeLabel={inFlight ? "Close checkout (unavailable while Townsy is confirming)" : "Close checkout"}
      footer={footer ?? undefined}
    >
      <div id="checkout-body" className="stack checkout" data-testid="checkout-dialog" data-step={state.step}>
        <div className="row small muted" style={{ gap: 6 }}>
          {live ? null : (
            <span className="badge badge-warning" data-testid="checkout-test-badge">
              Test mode
            </span>
          )}
          {kind ? <span>{KIND_LABEL[kind]}</span> : null}
        </div>
        {body}
      </div>
    </Dialog>
  );
}

function StatusLine({ text }: { text: string }) {
  return (
    <p className="small" role="status" style={{ margin: 0 }}>
      <span className="spinner" aria-hidden="true" /> {text}
    </p>
  );
}

function CloseButton({ onClose, label, primary = false }: { onClose(): void; label: string; primary?: boolean }) {
  return (
    <button type="button" className={primary ? "btn btn-primary" : "btn"} onClick={onClose}>
      {label}
    </button>
  );
}

export function confirmLabel(q: Pick<QuoteResponse, "cardAmountCents" | "creditAppliedCents">): string {
  if (q.cardAmountCents === 0) return `Buy with ${formatCents(q.creditAppliedCents)} Townsy Credit`;
  if (q.creditAppliedCents > 0) return `Continue: ${formatCents(q.creditAppliedCents)} credit + ${formatCents(q.cardAmountCents)} card`;
  return `Continue to card payment (${formatCents(q.cardAmountCents)})`;
}

/** The price snapshot the buyer confirms (docs/01 "Purchase review"). */
export function QuoteSummary({
  quote,
  now,
  expired,
  billboard = null,
  campaign = null,
  onChangeTaxAddress,
}: {
  quote: QuoteResponse;
  now: number;
  expired: boolean;
  billboard?: CheckoutBillboard | null;
  campaign?: CheckoutCampaign | null;
  /** ADR-0046: change the sales-tax location (a new quote follows). */
  onChangeTaxAddress?: () => void;
}) {
  const commercial = quote.kind === "COMMERCIAL_ACQUISITION";
  const rental = quote.kind === "BILLBOARD_RENTAL" && billboard !== null;
  const funding = quote.kind === "CAMPAIGN_FUNDING";
  const stripeTax = quote.tax?.source === "stripe_tax";
  const taxLines = (quote.tax?.lines ?? []).filter((l) => l.amountCents > 0);
  const place = [quote.tax?.country, quote.tax?.postalCode].filter(Boolean).join(" ");
  // One jurisdiction: its label alone ("Sales tax (US WA) 10.25%"); several: each with its amount.
  const taxDetail = taxLines.length === 1 ? taxLines[0]!.label : taxLines.map((l) => `${l.label}: ${formatCents(l.amountCents)}`).join("; ");
  return (
    <dl className="kv" data-testid="quote-summary">
      <dt>{KIND_LABEL[quote.kind]}</dt>
      <dd>
        <strong>{quote.description}</strong>
      </dd>
      {rental ? (
        <>
          <dt>Billboard</dt>
          <dd data-testid="quote-billboard">{billboard.label}</dd>
          <dt>Dates</dt>
          <dd data-testid="quote-billboard-dates">{formatUtcPeriod(billboard.startDate, billboard.endDate)}</dd>
          <dt>Design</dt>
          <dd>{billboard.creativeTitle}</dd>
        </>
      ) : null}
      {funding && campaign ? (
        <>
          <dt>{campaign.storePropertyId ? "Offers" : "Campaign"}</dt>
          <dd data-testid="quote-campaign">{campaign.title}</dd>
          <dt>Store</dt>
          <dd>{campaign.address}</dd>
        </>
      ) : null}
      {commercial ? (
        <>
          <dt>Current owner</dt>
          <dd>{quote.currentOwnerLabel ?? "Nobody (unowned or a Townsy demo store)"}</dd>
        </>
      ) : null}
      <dt>Price</dt>
      <dd className="price" data-testid="quote-subtotal">
        <strong>{formatCents(quote.subtotalCents)}</strong>
      </dd>
      {stripeTax ? (
        <>
          <dt>Sales tax</dt>
          <dd className="price" data-testid="quote-tax">
            {formatCents(quote.taxCents)}
            {place ? <span className="small muted"> · for {place}</span> : null}
            {onChangeTaxAddress ? (
              <>
                {" "}
                <button type="button" className="btn btn-ghost btn-sm" onClick={onChangeTaxAddress} data-testid="quote-tax-change">
                  Change
                </button>
              </>
            ) : null}
            {taxDetail || quote.taxCents > 0 ? (
              <div className="small muted" data-testid="quote-tax-lines">
                {taxDetail}
                {taxDetail && quote.taxCents > 0 ? " · " : ""}
                {quote.taxCents > 0 ? "paid by card, never with Townsy Credit" : ""}
              </div>
            ) : null}
          </dd>
          <dt>Total</dt>
          <dd className="price" data-testid="quote-total">
            {formatCents(quote.subtotalCents + quote.taxCents)}
          </dd>
        </>
      ) : (
        <>
          <dt>Tax</dt>
          <dd className="price" data-testid="quote-tax">
            {formatCents(quote.taxCents)}
            {quote.mode === "live" ? "" : " (test mode)"}
          </dd>
        </>
      )}
      <dt>Townsy Credit applied</dt>
      <dd className="price" data-testid="quote-credit">
        {formatCents(quote.creditAppliedCents)}
        {funding ? <span className="small muted"> · card only: Townsy Credit never funds a campaign</span> : null}
      </dd>
      <dt>Card pays</dt>
      <dd className="price" data-testid="quote-card">
        <strong>{formatCents(quote.cardAmountCents)}</strong>
        {quote.cardAmountCents === 0 ? <span className="small muted"> · no card needed</span> : null}
      </dd>
      {commercial ? (
        <>
          <dt>Your basis</dt>
          <dd className="price" data-testid="quote-basis">
            {formatCents(quote.reimbursableBasisCents)}
          </dd>
          <dt>Previous owner gets</dt>
          <dd data-testid="quote-displacement">
            {quote.displacementCreditCents && quote.displacementCreditCents > 0
              ? `${formatCents(quote.displacementCreditCents)} Townsy Credit`
              : "No credit (demo store or unowned)"}
          </dd>
        </>
      ) : null}
      <dt>Price held</dt>
      <dd data-testid="quote-expiry">{expired ? <span className="error-text">Expired — get a new price</span> : `${formatCountdown(msUntil(quote.expiresAt, now))} left`}</dd>
    </dl>
  );
}

function CommercialTerms({ quote }: { quote: QuoteResponse }) {
  const basis = formatCents(quote.reimbursableBasisCents ?? quote.subtotalCents);
  return (
    <ul className="small checkout-terms" style={{ margin: 0, paddingLeft: 18 }} data-testid="commercial-terms">
      <li>
        <strong>{TAKEOVER_CONCEPT}</strong>
      </li>
      <li>
        Your basis is {basis}: if someone takes this building over, you receive {basis} as Townsy Credit — the full amount, including any credit you
        use now{quote.taxCents > 0 ? " (sales tax is not part of the basis)" : ""}.
      </li>
      {quote.displacementCreditCents && quote.displacementCreditCents > 0 ? (
        <li>
          {quote.currentOwnerLabel ?? "The current owner"} receives {formatCents(quote.displacementCreditCents)} Townsy Credit when your purchase
          completes.
        </li>
      ) : (
        <li>Nobody receives Townsy Credit for this purchase (no previous paid owner).</li>
      )}
      <li>
        The building, its sign position and its address stay; the sign shows your name and “NEW” until you set up your store (name, logo, website) and it
        passes Townsy's check.
      </li>
      <li>You cannot buy your own building. Prices are server quotes; the browser never sets a price.</li>
    </ul>
  );
}

function PurchaseTerms({ quote }: { quote: QuoteResponse }) {
  return (
    <ul className="small checkout-terms" style={{ margin: 0, paddingLeft: 18 }}>
      {quote.kind === "RESIDENTIAL_PLOT" ? (
        <>
          <li>
            <strong>Homes can never be taken over by other players.</strong> Your home starts as a tent.
          </li>
          <li>Each account can own one plot.</li>
        </>
      ) : quote.kind === "HOME_UPGRADE" ? (
        <li>Upgrades go one level at a time and charge only the next level's price. Homes can never be taken over.</li>
      ) : quote.kind === "BILLBOARD_RENTAL" ? (
        <>
          <li>
            <strong>The board shows your approved design on these whole UTC days only.</strong> It switches at 00:00 UTC; nobody else can book the same days.
          </li>
          <li>No impression or traffic guarantee, no automatic renewal. Cancellation refunds are not offered yet.</li>
          <li>If Townsy revokes the design during the rental, the board shows the neutral placeholder.</li>
        </>
      ) : quote.kind === "CAMPAIGN_FUNDING" ? (
        <>
          <li>
            <strong>This adds the amount to your campaign's budget.</strong> Each completed task is charged from it at the price you set; tasks in
            progress reserve their price until they finish or expire.
          </li>
          <li>No guarantee of completions, visitors or customers. Budget refunds are not offered yet; unused budget stays with the campaign.</li>
          <li>If the store changes owners before this payment completes, the campaign cannot receive it and the card is refunded.</li>
        </>
      ) : (
        <li>The item goes to your storage; place it from My home. Items are never deleted.</li>
      )}
      {quote.kind === "CAMPAIGN_FUNDING" ? (
        <li>Card only: Townsy Credit can never fund a campaign. Coins are a separate balance and are not used here.</li>
      ) : (
        <li>Your Townsy Credit is applied first; a card pays the rest. Coins are a separate balance and are not used here.</li>
      )}
      <li>If the purchase cannot be completed after a card payment, the card is refunded — never replaced by credit.</li>
    </ul>
  );
}

/** Shown before every payment (founder request 2026-10-13): money paid into Townsy is final; the policy opens in a new tab. */
export function PaymentsFinal() {
  return (
    <p className="small" style={{ margin: 0 }} data-testid="checkout-payments-final">
      <strong>Payments are final.</strong> Money paid into Townsy cannot be withdrawn and is not refunded, except as the{" "}
      <a href="/legal/payments" target="_blank" rel="noopener">
        Payments &amp; Credit Policy
      </a>{" "}
      says (a purchase that cannot be completed is refunded to your card).
    </p>
  );
}

function CreditToggle({ visible, useCredit, availableCents, onChange }: { visible: boolean; useCredit: boolean; availableCents: number; onChange(v: boolean): void }) {
  if (!visible) return null;
  return (
    <label className="row small checkout-credit-toggle">
      <input type="checkbox" checked={useCredit} onChange={(e) => onChange(e.target.checked)} data-testid="checkout-use-credit" />
      <span>
        Apply my Townsy Credit first{availableCents > 0 ? ` (${formatCents(availableCents)} available)` : ""}. Changing this asks for a new price.
      </span>
    </label>
  );
}

function HoldLine({ order, now }: { order: OrderView; now: number }) {
  const ms = msUntil(order.reservationExpiresAt, now);
  return (
    <p className="checkout-hold" role="timer" aria-live="off" data-testid="checkout-hold" style={{ margin: 0 }}>
      {ms > 0 ? (
        <>
          Held for you: <strong>{formatCountdown(ms)}</strong>
          <span className="small muted"> — pay before the hold ends</span>
        </>
      ) : (
        <span className="error-text">The hold time is over. Checking with Townsy…</span>
      )}
    </p>
  );
}

function FailureNotice({ state }: { state: CheckoutState }) {
  if (!state.failure) return null;
  const tone = state.failure.action === "retry" ? "notice-warning" : "notice-danger";
  return (
    <div className={`notice ${tone}`} role="alert" data-testid="checkout-error" data-code={state.failure.code}>
      {state.failure.message}
    </div>
  );
}

function fulfilledLine(order: OrderView, subject: CheckoutSubject | null = null): string {
  switch (order.kind) {
    case "COMMERCIAL_ACQUISITION":
      return "The building is yours. Set up your store now: your name, logo, website and the sign inside, all on one screen.";
    case "RESIDENTIAL_PLOT":
      return "The plot is yours. Your home starts as a tent.";
    case "HOME_UPGRADE":
      return "Your home has been upgraded. Everyone in town sees it.";
    case "CATALOG_ITEM":
      return "The item is in your storage. Place it from My home.";
    case "BILLBOARD_RENTAL":
      return order.billboard
        ? `Booked: ${billboardName(order.billboard.billboardId)} shows your design ${formatUtcPeriod(order.billboard.startDate, order.billboard.endDate)}.`
        : "Your billboard days are booked. The board shows your design from the first day (00:00 UTC).";
    case "CAMPAIGN_FUNDING":
      // ADR-0027: offers saved on the store screen start by themselves (the server decides; this only says so).
      return subject?.campaign?.autoStart
        ? `${formatCents(order.subtotalCents)} was added to your offers' budget. They start by themselves once a Townsy reviewer has passed them.`
        : `${formatCents(order.subtotalCents)} was added to your campaign's budget. A campaign runs once Townsy has approved it and you activate it on the Tasks tab.`;
  }
}

/**
 * Right after buying a store, the primary next step (ADR-0020, founder feedback 2026-10-14): close the checkout and open
 * "Set up your store" (name, logo, website, description, the sign inside and the interior on one screen).
 */
export function SetUpStoreButton({ propertyId, onDone }: { propertyId: string; onDone(): void }) {
  const openStoreSetup = useTownStore((s) => s.openStoreSetup);
  return (
    <button
      type="button"
      className="btn btn-primary"
      onClick={() => {
        onDone();
        openStoreSetup(propertyId, "purchase");
      }}
      data-testid="checkout-set-up-store"
    >
      Set up your store
    </button>
  );
}

/** ADR-0027: the budget was paid from the store's "Add an offer" screen: back to it (its status says what happens next). */
export function BackToOffersButton({ propertyId, onDone }: { propertyId: string; onDone(): void }) {
  const openOfferSetup = useTownStore((s) => s.openOfferSetup);
  return (
    <button
      type="button"
      className="btn btn-primary"
      onClick={() => {
        onDone();
        openOfferSetup(propertyId, "checkout");
      }}
      data-testid="checkout-open-offers"
    >
      Back to your offers
    </button>
  );
}

/** Right after buying a store: close the checkout and open the interior picker for it (also on the store screen). */
export function ChooseInteriorButton({ propertyId, onDone }: { propertyId: string; onDone(): void }) {
  const openThemePicker = useTownStore((s) => s.openThemePicker);
  return (
    <button
      type="button"
      className="btn"
      onClick={() => {
        onDone();
        openThemePicker(propertyId, "purchase");
      }}
      data-testid="checkout-choose-interior"
    >
      Choose interior
    </button>
  );
}

function FollowUp({ order, subject, onDone, hasSession }: { order: OrderView; subject: CheckoutSubject | null; onDone(): void; hasSession: boolean }) {
  const session = useTownSession();
  if (order.kind === "BILLBOARD_RENTAL") {
    return (
      <Link className="btn btn-primary" to="/advertise?tab=billboards" onClick={onDone}>
        My billboard bookings
      </Link>
    );
  }
  if (order.kind === "CAMPAIGN_FUNDING") {
    const store = subject?.campaign?.storePropertyId ?? null;
    if (store) return <BackToOffersButton propertyId={store} onDone={onDone} />;
    const id = order.campaignId ?? subject?.campaign?.campaignId ?? null;
    return (
      <Link className="btn btn-primary" to={`/advertise?tab=tasks${id ? `&campaign=${encodeURIComponent(id)}` : ""}`} onClick={onDone} data-testid="checkout-open-campaign">
        Open my campaign
      </Link>
    );
  }
  if (order.kind === "CATALOG_ITEM") {
    const tab = subject?.itemKind === "YARD" ? "yard" : "interior";
    return hasSession && session ? (
      <button
        type="button"
        className="btn btn-primary"
        onClick={() => {
          onDone();
          session.openMyHome(tab);
        }}
      >
        Place it
      </button>
    ) : (
      <Link className="btn btn-primary" to="/directory#my-home" onClick={onDone}>
        Place it
      </Link>
    );
  }
  if (!order.propertyId) return null;
  const id = order.propertyId;
  // A bought store's primary next step is setting it up (SetUpStoreButton).
  const cls = order.kind === "COMMERCIAL_ACQUISITION" ? "btn" : "btn btn-primary";
  return hasSession && session ? (
    <button
      type="button"
      className={cls}
      onClick={() => {
        onDone();
        session.goToProperty(id);
      }}
    >
      Go there
    </button>
  ) : (
    <Link className={cls} to={`/place/${id}`} onClick={onDone}>
      See it in the town
    </Link>
  );
}
